import { memo, useCallback } from 'react';
import { NodeToolbar, Position } from 'reactflow';
import {
  Maximize2,
  Trash2,
  Copy,
  Library,
  Database,
  FileText,
} from 'lucide-react';
import useFlowStore from '../../stores/useFlowStore';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Bottom-right toolbar on entities (React Flow Node Toolbar pattern).
 * @see https://reactflow.dev/examples/nodes/node-toolbar
 */
const EntityNodeToolbar = ({ id, selected }) => {
  const { userHasRole } = useAuth();
  const selectedNodes = useFlowStore((s) => s.selectedNodes);
  const nodes = useFlowStore((s) => s.nodes);
  const openEntityEditModalForNode = useFlowStore((s) => s.openEntityEditModalForNode);
  const requestDeleteEntity = useFlowStore((s) => s.requestDeleteEntity);
  const requestEntityToolbarAction = useFlowStore((s) => s.requestEntityToolbarAction);

  const node = nodes.find((n) => n.id === id);
  const canManageIndexes = node?.type !== 'text' && node?.data?.fields;

  const onlySingleSelection =
    selectedNodes.length === 1 && selectedNodes[0]?.id === id;
  const isVisible = selected && onlySingleSelection;

  const onEdit = useCallback(
    (e) => {
      e.stopPropagation();
      openEntityEditModalForNode(id);
    },
    [id, openEntityEditModalForNode]
  );

  const onDelete = useCallback(
    (e) => {
      e.stopPropagation();
      requestDeleteEntity(id);
    },
    [id, requestDeleteEntity]
  );

  const onDuplicate = useCallback(
    (e) => {
      e.stopPropagation();
      requestEntityToolbarAction(id, 'duplicate');
    },
    [id, requestEntityToolbarAction]
  );

  const onSaveDomain = useCallback(
    (e) => {
      e.stopPropagation();
      requestEntityToolbarAction(id, 'saveDomain');
    },
    [id, requestEntityToolbarAction]
  );

  const onManageIndexes = useCallback(
    (e) => {
      e.stopPropagation();
      requestEntityToolbarAction(id, 'indexes');
    },
    [id, requestEntityToolbarAction]
  );

  const onBusinessRules = useCallback(
    (e) => {
      e.stopPropagation();
      requestEntityToolbarAction(id, 'rules');
    },
    [id, requestEntityToolbarAction]
  );

  const indexCount = node?.data?.indexes?.length ?? 0;

  return (
    <NodeToolbar
      nodeId={id}
      isVisible={isVisible}
      position={Position.Bottom}
      align="end"
      offset={6}
      className="flex flex-wrap items-center justify-end gap-0.5 max-w-[min(100vw-2rem,22rem)] rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-md p-0.5 nodrag nopan"
    >
      <button
        type="button"
        onClick={onEdit}
        onPointerDown={(e) => e.stopPropagation()}
        className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 transition-colors"
        title="Edit in full modal"
      >
        <Maximize2 size={16} aria-hidden />
      </button>
      <button
        type="button"
        onClick={onDuplicate}
        onPointerDown={(e) => e.stopPropagation()}
        className="p-1.5 rounded hover:bg-green-50 dark:hover:bg-green-900/30 text-green-700 dark:text-green-400 transition-colors"
        title="Duplicate Entity"
      >
        <Copy size={16} aria-hidden />
      </button>
      <button
        type="button"
        onClick={onSaveDomain}
        onPointerDown={(e) => e.stopPropagation()}
        disabled={!userHasRole}
        className={`p-1.5 rounded transition-colors ${
          userHasRole
            ? 'hover:bg-purple-50 dark:hover:bg-purple-900/30 text-purple-700 dark:text-purple-300'
            : 'opacity-40 cursor-not-allowed text-gray-400'
        }`}
        title={
          userHasRole
            ? 'Save as Object'
            : 'You need an active subscription to save objects'
        }
      >
        <Library size={16} aria-hidden />
      </button>
      {canManageIndexes && (
        <button
          type="button"
          onClick={onManageIndexes}
          onPointerDown={(e) => e.stopPropagation()}
          className="relative p-1.5 rounded hover:bg-amber-50 dark:hover:bg-amber-900/30 text-amber-700 dark:text-amber-300 transition-colors"
          title={`Manage Indexes (${indexCount} indexes)`}
        >
          <Database size={16} aria-hidden />
          {indexCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[1rem] h-4 px-0.5 flex items-center justify-center leading-none">
              {indexCount}
            </span>
          )}
        </button>
      )}
      <button
        type="button"
        onClick={onBusinessRules}
        onPointerDown={(e) => e.stopPropagation()}
        className="relative p-1.5 rounded hover:bg-indigo-50 dark:hover:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 transition-colors"
        title="Business Rules"
      >
        <FileText size={16} aria-hidden />
        {(node?.data?.businessRules?.entity ||
          Object.keys(node?.data?.businessRules?.fields || {}).length > 0) && (
          <span className="absolute -top-0.5 -right-0.5 bg-green-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center leading-none">
            ✓
          </span>
        )}
      </button>
      <button
        type="button"
        onClick={onDelete}
        onPointerDown={(e) => e.stopPropagation()}
        className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 transition-colors"
        title="Delete Entity"
      >
        <Trash2 size={16} aria-hidden />
      </button>
    </NodeToolbar>
  );
};

export default memo(EntityNodeToolbar);
